import _ from "lodash"
import $ from "cash-dom"
import urlHelper from "./url_helper"
import platform from "platform"
import Cookies from "js-cookie"
import superProperties from "./super_properties"
import loadInspectlet from "./inspectlet_loader"
import storage from "./storage"
import signals from "./signals"
import amplitude from "amplitude-js"
import pageLoad from "./page_load"
import timeouts from "./timeouts"
import "./analytics_loader"
import {isVisible} from "./dom_utils"

let locale

const trackedEvents = {}

pageLoad.onReady(() => {
  // noqa=cyclomatic_complexity
  let landing
  initLinkTracking()

  function _initSuperProperties(superProperties, user) {
    // once something is set in super properties it stays there.
    // we need to clean-up unused / incorrectly set properties ...
    let toRemove = [
      "$created",
      "$email",
      "$initial_referrer",
      "$initial_referring_domain",
      "$name",
      "$search_engine",
      "$username",
      "__cmpns.368937",
      "__cmpns.719239",
      "__mpap",
      "__mps.ab_test_identifier",
      "ab_test_identifier",
      "articles_read_count",
      "atlas_quiz_count",
      "cancellation_followup",
      "change_term_language",
      "current_page_path",
      "demo_quiz_started_count",
      "email_ebook_after_registration",
      "first_seen_TRL-1862",
      "first_seen_TRL-1880",
      "first_seen_TRL-1921",
      "first_seen_TRL-1948",
      "first_seen_TRL-1948-4",
      "first_seen_TRL-1948-6",
      "first_seen_TRL-1993",
      "first_seen_TRL-2048",
      "first_seen_TRL-2068",
      "first_seen_TRL-2218",
      "first_seen_learning_pathway",
      "first_seen_learning_pathway_2",
      "free_quiz_started_count",
      "inactive_subscription_count",
      "integrated_quiz",
      "kenhub",
      "keyboard_in_word_salad_changed",
      "last_events",
      "last_subscription_end_at",
      "last_subscription_end_date",
      "learning_pathway",
      "max_tour_step",
      "mp_keyword",
      "mp_name_tag",
      "muscle_facts_quiz_started_count",
      "other_quiz_started_count",
      "plan",
      "premium_quiz_started_count",
      "question_bank_quiz_started_count",
      "quiz_started_count",
      "rc_token",
      "set_level_before_training",
      "subscription_started_at",
      "subscription_started_date",
      "temp_premium_recommendation",
      "trainer_design",
      "trial_ends_in",
      "trl-2123",
      "undefined_quiz_started_count",
      "user-agent",
      "video_played_count",
      "videos_played_count",
      "was_on_trial",
      "alreadyTrained",
      "articlesConsumedCount",
      "authenticationMethods",
      "confirmedAt",
      "createdAt",
      "developingCountry",
      "disposableEmail",
      "doNotDisturb",
      "heardFrom",
      "nonPayingDomain",
      "subscriptionStartedAt",
      "subscriptionStartedDate",
      "userAgent",
      "fieldOfStudy",
      "inactiveSubscriptionCount",
      "lastSubscriptionEndAt",
      "lastSubscriptionEndDate",
      "messageWaiting",
      "netRevenueUsd",
      "quizzesConsumedCount",
      "skipAtlasWatermark",
      "ssoLastLoggedInAt",
      "studyInstitute",
      "termLanguage",
      "trialStartedAt",
      "userId",
      "videosConsumedCount",
      "workInstitute",
      "workInstituteType"
    ]
    if (user) toRemove = _.union(toRemove, _.keys(user))
    toRemove.map((prop) => superProperties.remove(prop))
  }

  function _loadUserExperimentsToSuperProperties(user) {
    if (!user) return

    const superPropertiesExperiments =
      superProperties.get("experiments") || []
    const userExperiments = user?.experiments || []
    const experiments = _.union(superPropertiesExperiments, userExperiments)
    superProperties.register({experiments: experiments})
  }

  /* analytics */
  const {currentUser} = window.gon
  locale = window.gon.locale

  function initUser(user) {
    if (!user) return

    user = _.mapKeys(user, (_v, k) => _.snakeCase(k))
    _initSuperProperties(superProperties, user)
    _loadUserExperimentsToSuperProperties(user)
    _amplitudeIdentify(user)
    _customerioIdentify(user)
    _gtagIdentify(user)
    superProperties.register(user)
  }

  function _customerioIdentify(user) {
    const userProperties = _.merge({}, superProperties.getAll(), user)
    // customer.io requires an id key for user
    userProperties.id = userProperties.user_id
    return window._cio.identify(userProperties)
  }

  function _gtagIdentify(user) {
    window.gtag("user_id", user.user_id)
  }

  function _amplitudeIdentify(user) {
    amplitude.getInstance().setUserId(`${user.user_id}`)
    // merging with super properties
    // omitting personal identifying data like email, name
    const userProperties = _
      .chain({})
      .merge(superProperties.getAll(), user)
      .omit(["email", "name", "username"])
      .value()
    // based on
    // https://github.com/amplitude/Amplitude-Javascript/blob/
    // 7fcf451ec1380266970a556bcda2d75ba5103873/src/
    // amplitude-client.js#L652-L664
    const identify = new amplitude.Identify()
    for (const k in userProperties) {
      const v = userProperties[k]
      if (v !== null) identify.set(k, v)
      if (v === null) identify.unset(k, v)
    }
    return amplitude.getInstance().identify(identify)
  }

  function _updateSuperProperty(prop, value, once = false) {
    const update = {}
    update[prop] = value
    if (!once) superProperties.register(update)
    if (once) superProperties.registerOnce(update)
  }

  function _incrementSuperProperty(prop) {
    let value = superProperties.get(prop)
    if (typeof value !== "number") value = 0
    value += 1
    return _updateSuperProperty(prop, value)
  }

  // # Payment

  function trackSubscriptionForGoogleAnalytics(order) {
    if (!order) return

    /* eslint-disable camelcase */
    window.gtag("event", "subscription started")
    window.gtag("event", "purchase", {
      transaction_id: order.id,
      affiliation: "Kenhub",
      value: order.amount,
      currency: order.currency.toUpperCase(),
      items: [
        {
          item_id: order.priceSlug,
          item_name: "Kenhub Premium",
          sku: order.period,
          price: order.amount,
          currency: order.currency.toUpperCase(),
          quantity: 1
        }
      ]
    })
    /* eslint-enable camelcase */
  }

  _updateSuperProperty("platform", platform?.description || undefined)
  _updateSuperProperty("userAgent", platform?.ua || undefined)
  _updateSuperProperty("last_page_referrer", document.referrer)

  // is set when user has just registered
  // see ApplicationController.trackRegistration
  if (window.gon.registeredWithProvider) {
    // eslint-disable-next-line no-console
    console.log(`Registration Success ${window.gon.registeredWithProvider}`)

    // converting an anonymous user to a real one
    initUser(currentUser)
    track("registration success", {
      label: window.gon.registeredWithProvider,
      category: "registration"
    })
  } else {
    initUser(currentUser)
  }

  // track first landing page type (e.g. atlas, videos)
  if (!currentUser) {
    landing = window.gon.tracking?.landing
    if (landing) _updateSuperProperty("landing_page_type", landing, true)
  }

  const eventListeners = {
    "kenhub.subscription_started": (order) => {
      trackSubscriptionForGoogleAnalytics(order)
    },
    "kenhub.quiz_started": (properties) => {
      track("quiz started", properties)
    },
    "kenhub.payment_failed": (properties) => {
      track("payment failed", properties)
    },
    "kenhub.quiz_start_clicked": (properties) => {
      track("quiz start clicked", properties)
    },
    "kenhub.payment_initiated": (properties) => {
      track("payment initiated", properties)
    },
    "kenhub.quiz_summary_seen": (properties) => {
      track("quiz summary seen", properties)
    },
    // Algolia search
    "kenhub.search_performed": (props) => {
      track("search page viewed", props)
    },
    // change term language
    "kenhub.terminology_changed": (props) => {
      _incrementSuperProperty("change_term_language")
      track("terminology changed", props)
    },
    // keyboard in word salad changed
    "kenhub.keyboard_in_word_salad_changed": (props) => {
      _incrementSuperProperty("keyboard_in_word_salad_changed")
      track("keyboard in word salad changed", props)
    },
    // video play
    "kenhub.video_played": (videoAttributes) => {
      if (!videoAttributes) return

      track("video played", videoAttributes)
    },
    // custom quz copy button clicked
    "kenhub.custom_quiz_copy_button_clicked": (props) => {
      track("custom quiz copy button clicked", props)
    },
    // custom quiz started
    "kenhub.custom_quiz_started": (props) => {
      _incrementSuperProperty("custom_quiz_started_count")
      const permittedParams = [
        "slug",
        "userId",
        "ownedBy",
        "performedBy",
        "selection",
        "quizIds",
        "topicIds",
        "length",
        "duration",
        "muscleFacts",
        "spellingExercises",
        "excludedMuscleFacts",
        "excludedLearnableSlugs",
        "trainingSessionSlug",
        "containerSlugs",
        "contentTypes",
        "relationTypes",
        "version",
        "level",
        "locale"
      ]
      const attrs = _.pick(props, permittedParams)
      // amplitude can't have an object as a parameter, therefore we will just
      // use the added learnables and omit the lectures
      if (props.addedLearnablesAndLectures) {
        attrs.addedLearnables =
          Object.keys(props.addedLearnablesAndLectures)
      }
      track("custom quiz started", attrs)
    },
    "kenhub.changelog_clicked": (props) => {
      track("changelog clicked", props)
    },
    "kenhub.content_consumed": (props) => {
      track("content consumed", props)
    },
    "kenhub.stripe_payment_request_button": (result) => {
      const available = !!result
      _updateSuperProperty(
        "stripe_payment_request_button",
        _.merge({available}, result)
      )
      track("stripe payment request button", {available})
    }
  }

  // trigger listeners for old events that happened while tracking was loading
  for (const event of signals.history) {
    const listener = eventListeners[event.name]
    if (listener) listener(event.props)
  }
  signals.history = []

  // register listeners to new events
  for (const key in eventListeners) {
    signals.on(key, eventListeners[key])
  }

  registerLink("download button clicked", el => ({
    name: $(el).attr("href").split("/").pop(),
    href: $(el).attr("href")
  }))
  registerLink("external link clicked", el => ({
    href: $(el).attr("href")
  }))
  registerLink("pricing page clicked")
  registerLink("registration clicked", (el) => {
    const registrationElement = $(el).data("track-element")
    landing = window.gon.tracking?.landing
    _updateSuperProperty("registration_source", registrationElement)
    if (landing) _updateSuperProperty("registration_page_type", landing)
    return {element: registrationElement, pageType: landing}
  })
  registerLink("ultimate study guide downloaded")

  // track page views. using gon.tracking.pageView
  window.gtag("event", "page_view")
  switch (window.gon.tracking?.pageView) {
    case "home page viewed":
      track("home page viewed")
      break
    case "landing page viewed":
      track("landing page viewed", window.gon.tracking.landingPage)
      break
    case "quiz summary seen":
      track("quiz summary seen", window.gon.tracking.quiz)
      break
    case "custom quiz summary seen":
      track("custom quiz summary seen", window.gon.tracking.customQuiz)
      break
    case "article read":
      track("article read", window.gon.tracking.article)
      break
    case "article index":
      track("article index")
      break
    case "quiz page viewed":
      track("quiz page viewed", window.gon.tracking.quiz)
      break
    case "atlas term viewed":
      track("atlas term viewed", window.gon.tracking.atlasTerm)
      _incrementSuperProperty("atlas_term_viewed_count")
      break
    case "video page viewed":
      track("video page viewed")
      break
    case "video index":
      track("video index")
      break
    case "study unit viewed":
      track("study unit viewed", window.gon.tracking.studyUnit)
      break
    case "listing page viewed":
      track("listing page viewed", window.gon.tracking.listingPage)
      break
    case "dashboard viewed":
      track("dashboard viewed")
      break
    case "pricing page visited":
      track("pricing page visited")
      break
    case "checkout page visited":
      track("checkout page visited", {period: window.gon.tracking.period})
      break
    case "course deal":
      track("course deal page visited")
      break
    case "custom quiz index":
      track("custom quiz index")
      break
    case "custom quiz page viewed":
      track("custom quiz page viewed", window.gon.tracking.customQuiz)
      break
    case "custom quiz creator viewed":
      track("custom quiz creator viewed", window.gon.tracking.customQuiz)
      break
    case "success stories page viewed":
      track(
        "success stories page viewed",
        {storyPageType: window.gon.tracking?.pageType}
      )
      break
    case "help page viewed":
      track("help page viewed")
      break
    case "registration page viewed":
      track(
        "registration page viewed",
        {registrationPageType: window.gon.tracking?.pageType}
      )
      break
  }

  // search count
  $("form[action$='search']").on("submit", () =>
    _incrementSuperProperty("search_count")
  )

  // utm tags
  function _updateUtmSuperProperty(prop, value) {
    _updateSuperProperty(`first_${prop}`, value, true)
    return _updateSuperProperty(`last_${prop}`, value, false)
  }

  // if utm_source is present in url query,
  // we update all other utm super properties
  const urlQuery = urlHelper.getQueryParams()
  if (urlQuery?.utm_source) {
    for (const utmProp of [
      "utm_campaign",
      "utm_source",
      "utm_medium",
      "utm_content"
    ]) {
      _updateUtmSuperProperty(utmProp, urlQuery[utmProp])
    }
  }

  if (currentUser) _updateSuperProperty("currency", window.gon.currency)

  // web sessions
  if (!Cookies.get("__khs")) {
    signals.emit("kenhub.new_web_session")
    _incrementSuperProperty("web sessions")
    // setting a session cookie
    // (will be cleared automatically when browser is closed)
    Cookies.set("__khs", "active")
  }

  // loading inspectlet if feature flag is set
  if (window.gon.inspectlet) inspectletForPtCheckoutPage()

  if (isVisible($(".lp__learning-pathway")[0])) {
    inspectletPush(["tagSession", "with learning path"])
  }
})

const linkTracking = {}

function initLinkTracking() {
  // catches any click event
  $(document).on("click", (e) => {
    if (!e?.target) return
    // search for ancestors with this data attribute including itself
    const el = $(e.target).closest("[data-track]")
    if (!el.length) return
    // forms get tracked differently with register_form
    if (el.is("form")) return

    const properties = linkTracking[$(el).data("track")]
    tracking.trackLink(e, el, properties)
  })
}

function inspectletForPtCheckoutPage() {
  if (!window.gon.currentUser) return
  // only on the checkout page for portuguese users
  if (!window.location.pathname.match(/^\/pt\/checkout/)) return
  loadInspectlet()
  inspectletPush(["tagSession", "pt_checkout_page"])
}

function inspectletPush(event) {
  if (typeof __insp === "undefined") return

  window.__insp.push(event)
}

function registerLink(identifier, properties) {
  linkTracking[identifier] = properties || {}
}

function _trackData(el, properties) {
  const data = $(el).data()
  properties = _executeIfFunction(properties, el) || {}
  _.merge(properties, _getDataProperties(data))
  const eventName = data.track.split(" ").join("_").toLowerCase()
  signals.emit(`kenhub.${eventName}`, properties)
  track(data.track, properties)
}

function _executeIfFunction(el, ...args) {
  if (typeof el === "function") return el(...args)
  return el
}

function _getDataProperties(data) {
  return _.reduce(
    data,
    (result, value, key) => {
      const trackingKey = key.replace(/^track/, "")
      if (trackingKey && trackingKey !== key) {
        result[trackingKey.toLowerCase()] = value
      }

      return result
    },
    {}
  )
}

function track(trackingEvent, properties = {}) {
  properties.locale = locale
  properties.path = window.location.pathname
  properties = _.mapKeys(properties, (_v, k) => _.snakeCase(k))
  const propertiesWithoutNull =
    _.omitBy(properties, (v, _k) => v === null)
  amplitude.getInstance().logEvent(trackingEvent, propertiesWithoutNull)
  window._cio.track(trackingEvent, properties)
  window.gtag("event", trackingEvent, propertiesWithoutNull)
  _saveEventForTesting(trackingEvent, properties)
}

function _saveEventForTesting(event, properties = {}) {
  if (!window.gon.javascriptTestEnv) return
  if (!storage.enabled) return

  const events = storage.get("_kh_gi") || []
  events.unshift(_.extend({name: event}, properties))
  // eslint-disable-next-line no-console
  console.log(JSON.stringify(events))
  storage.set("_kh_gi", events)
}

const tracking = {
  trackPageView: (url) => {
    window.gtag("event", "page_view", {pageLocation: url})
  },
  trackLink: (e, el, properties) => {
    // when passing React's syntheticEvent from its callback,
    // `currentTarget` goes null inside the setTimeout because of issues
    // related to https://reactjs.org/docs/events.html#event-pooling
    // so extract `currentTarget` before the async event
    const href = $(el).attr("href")
    const target = $(el).attr("target")
    _trackData(el, properties)
    // lets the event to continue bubbling up if the url has the same domain
    // and we don't prevent turbo
    // note: turbo can still decide to do a reload but this will happen
    // after fetching the next page and then enough time has passed
    if (
      urlHelper.isSameDomain(href) &&
      !pageLoad.preventTurbo(href)
    ) {
      return true
    }

    timeouts.set(() => {
      // we defer the preventDefault decision to later because there might be
      // other callbacks which got registered later and might change it as well
      if (e.preventDefaultBehaviour) return

      if (target === "_blank") {
        window.open(href)
        return
      }
      window.location = href
    }, 500)
    return e.preventDefault()
  },

  // Marks the event as being tracked, stops the propagation and triggers the
  // event again after a timeout which then can propagate in a normal way
  // since the event was already marked
  trackForm: (e) => {
    const trackingEvent = $(e.currentTarget).data("track")
    // this will skip the tracking for this event when the submit callback
    // was triggered after the timeout
    if (trackedEvents[trackingEvent]) {
      delete trackedEvents[trackingEvent]
      return true
    }
    trackedEvents[trackingEvent] = true
    _trackData(e.currentTarget, e.data?.properties)
    const $currentTarget = $(e.currentTarget)
    timeouts.set(() => $currentTarget.trigger("submit"), 500)
    // prevents calling any further callbacks for the same element
    e.stopImmediatePropagation()
    return false
  }
}

export default tracking
